export type ProgressEvent = {
  kind: 'progress';
  runId?: string;
  agent: 'orchestrator' | 'planner' | 'codegen' | 'sandbox' | 'insight' | 'qa' | string;
  phase: 'analysis:plan' | 'exec:start' | 'exec:done' | 'eval:start' | 'eval:done' | 'analysis:complete' | string;
  msg?: string;
  ts?: number;
};

export type TerminalEvent =
  | { kind: 'run_finished'; runId?: string; ok: true; ts?: number }
  | { kind: 'run_failed'; runId?: string; ok: false; ts?: number; error: { code: string; message: string; hint?: string } };

export type ChipState = 'creating-recipes' | 'cooking' | 'tasting' | 'done' | 'failed';

export function mapToChip(e: ProgressEvent | TerminalEvent): ChipState {
  if ((e as any)?.kind === 'run_failed') return 'failed';
  if ((e as any)?.kind === 'run_finished') return 'done';
  const p = e as ProgressEvent;
  if (p && p.agent) {
    // Planner and orchestrator always map to creating-recipes
    if (p.agent === 'orchestrator' || p.agent === 'planner') return 'creating-recipes';
    // Codegen maps to creating-recipes (but will display as "Collecting ingredients" in UI)
    if (p.agent === 'codegen') return 'creating-recipes';
    // Only treat sandbox as cooking when explicitly in exec phases
    if (p.agent === 'sandbox' && (p.phase === 'exec:start' || p.phase === 'exec:done')) return 'cooking';
    if (p.agent === 'insight' || p.agent === 'qa') return 'tasting';
  }
  return 'creating-recipes';
}

export function advanceMonotonic(prev: ChipState | null, next: ChipState, event?: ProgressEvent): ChipState {
  const order: Record<ChipState, number> = {
    'creating-recipes': 0,
    'cooking': 1,
    'tasting': 2,
    'done': 3,
    'failed': 4,
  };
  if (prev == null) return next;
  
  // Allow resetting from 'cooking' back to 'creating-recipes' when codegen retry is detected
  // This happens when we see a codegen event after already being in cooking state
  // The message will contain "again" or "adjusting" to indicate a retry
  if (prev === 'cooking' && next === 'creating-recipes' && event?.agent === 'codegen') {
    const msg = String(event?.msg || (event as any)?.message || '').toLowerCase();
    if (msg.includes('again') || msg.includes('adjusting')) {
      return next; // Allow reset to show retry
    }
  }
  
  return order[next] >= order[prev] ? next : prev;
}


